import type {
  AskHelenaPendingWriteInput,
  AskHelenaSseEvent,
  AskHelenaStreamInput,
} from "shared";
import { debugConsole } from "utilz/helpers";

const ASK_BASE_URL = import.meta.env.VITE_APP_HELENA_ASK_BASE_URL;

const SSE_CONTENT_TYPE = "text/event-stream";

/**
 * Deliberate divergence from the api-layer convention: this endpoint streams,
 * so it uses `fetch` rather than `axiosClient` (axios cannot expose a
 * ReadableStream in the browser). That means `apiErrorHandler` does not apply —
 * it branches on `isAxiosError` and otherwise reads axios-only fields, so
 * feeding it a fetch Response would need a cast asserting a shape that doesn't
 * exist. The only downstream contract that matters is `getApiErrorMessage`,
 * which needs nothing more than `error.message`, so we throw a plain Error.
 */
export class AskHelenaHttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AskHelenaHttpError";
    this.status = status;
  }
}

/**
 * Carries the status as a field rather than only in the message: callers must
 * branch on 404 (the pending-write "no longer available" case), and sniffing
 * for "404" inside prose breaks the moment the service rewords a message.
 */
const toAskHelenaError = (status: number, rawBody: string): Error => {
  try {
    const parsed: unknown = JSON.parse(rawBody);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "message" in parsed &&
      typeof (parsed as { message: unknown }).message === "string"
    ) {
      return new AskHelenaHttpError(
        (parsed as { message: string }).message,
        status,
      );
    }
  } catch {
    // Non-JSON body (proxy error page) — fall through to the status message.
  }
  return new AskHelenaHttpError(
    `Ask Helena request failed with status ${status}`,
    status,
  );
};

const isAskHelenaSseEvent = (value: unknown): value is AskHelenaSseEvent => {
  if (typeof value !== "object" || value === null || !("type" in value)) {
    return false;
  }
  const { type } = value as { type: unknown };
  return (
    type === "conversation" ||
    type === "token" ||
    type === "tool_call" ||
    type === "tool_result" ||
    type === "awaiting_confirmation" ||
    type === "done" ||
    type === "error"
  );
};

/**
 * Joins a frame's `data:` lines, per the SSE spec. Returns "" when the frame
 * carries no payload (a bare comment or keep-alive).
 *
 * The `event:` line is ignored on purpose: the contract carries the
 * discriminator inside the JSON payload as `data.type`.
 */
const extractFrameData = (frame: string): string => {
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    // Comment / keep-alive heartbeat.
    if (line.startsWith(":")) continue;

    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) continue;
    if (line.slice(0, separatorIndex) !== "data") continue;

    const value = line.slice(separatorIndex + 1);
    dataLines.push(value.startsWith(" ") ? value.slice(1) : value);
  }

  return dataLines.join("\n");
};

/**
 * Splits the accumulated buffer into complete SSE frames, returning the
 * trailing partial frame as `rest`. A network chunk can end mid-frame, so the
 * caller must feed `rest` back in on the next read — that is the whole point.
 */
const parseSseFrames = (
  buffered: string,
): { events: AskHelenaSseEvent[]; rest: string } => {
  // Normalise over the WHOLE buffer, not per chunk, so a \r that lands on a
  // chunk boundary is joined with its \n once that arrives.
  const normalised = buffered.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  const frames = normalised.split("\n\n");
  const rest = frames.pop() ?? "";
  const events: AskHelenaSseEvent[] = [];

  for (const frame of frames) {
    const data = extractFrameData(frame);
    if (!data) continue;

    try {
      const parsed: unknown = JSON.parse(data);
      // An unrecognised type is skipped rather than thrown, so a future
      // backend event cannot break an existing client.
      if (isAskHelenaSseEvent(parsed)) events.push(parsed);
    } catch (error: unknown) {
      debugConsole("Ask Helena: skipped an unparsable SSE frame", error);
    }
  }

  return { events, rest };
};

/**
 * Opens an SSE stream and delivers every parsed event to `onEvent`. Shared by
 * `/ask`, `/confirm` and `/cancel` — all three speak the same event union, so
 * there is exactly one reader.
 *
 * Errors arrive on two distinct channels and must stay distinct:
 *  - 400/401/404 land BEFORE the stream as ordinary JSON and are THROWN.
 *  - An `error` SSE frame is delivered through `onEvent`, NOT thrown, because
 *    `done` still follows it and the turn has to terminate normally.
 */
const readAskHelenaStream = async ({
  path,
  body,
  accessToken,
  signal,
  onEvent,
}: {
  path: string;
  body?: unknown;
  accessToken: string;
  signal: AbortSignal;
  onEvent: (event: AskHelenaSseEvent) => void;
}): Promise<void> => {
  const response = await fetch(`${ASK_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body ?? {}),
    signal,
  });

  if (!response.ok) {
    throw toAskHelenaError(response.status, await response.text());
  }

  // A proxy can answer 200 with an HTML error page; entering the read loop on
  // that would silently produce zero events instead of a visible failure.
  if (!response.headers.get("content-type")?.includes(SSE_CONTENT_TYPE)) {
    throw toAskHelenaError(response.status, await response.text());
  }

  if (!response.body) {
    throw new Error("Ask Helena returned no response body");
  }

  const reader = response.body.getReader();
  // ONE decoder for the whole stream: a multi-byte character can split across
  // network chunks, and a per-chunk decoder would yield U+FFFD.
  const decoder = new TextDecoder("utf-8");
  let buffered = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffered += decoder.decode(value, { stream: true });
      const { events, rest } = parseSseFrames(buffered);
      buffered = rest;
      for (const event of events) onEvent(event);
    }

    // Flush any trailing multi-byte sequence, then close the final frame —
    // servers commonly omit the terminating blank line.
    buffered += decoder.decode();
    const { events } = parseSseFrames(`${buffered}\n\n`);
    for (const event of events) onEvent(event);
  } finally {
    reader.releaseLock();
  }
};

export const streamAskHelena = async ({
  question,
  conversationId,
  voiceSessionId,
  accessToken,
  signal,
  onEvent,
}: AskHelenaStreamInput): Promise<void> =>
  readAskHelenaStream({
    path: "/api/v1/ask",
    body: { question, conversationId, voiceSessionId },
    accessToken,
    signal,
    onEvent,
  });

/**
 * Approves a proposed change: performs it, then streams the REST of the same
 * turn — typically `tool_call`, `tool_result`, `token`, `done`.
 *
 * A 404 is the answer to every kind of "no" here: already confirmed, already
 * cancelled, expired, someone else's, or never existed. The service refuses to
 * distinguish them, so neither can the UI — and it must never auto-retry, since
 * a failure mid-stream may mean the change already landed.
 */
export const streamAskHelenaConfirm = async ({
  pendingWriteId,
  accessToken,
  signal,
  onEvent,
}: AskHelenaPendingWriteInput): Promise<void> =>
  readAskHelenaStream({
    path: `/api/v1/pending-writes/${encodeURIComponent(pendingWriteId)}/confirm`,
    accessToken,
    signal,
    onEvent,
  });

/** Declines it. Streams briefly so the thread records the refusal; costs no model call. */
export const streamAskHelenaCancel = async ({
  pendingWriteId,
  accessToken,
  signal,
  onEvent,
}: AskHelenaPendingWriteInput): Promise<void> =>
  readAskHelenaStream({
    path: `/api/v1/pending-writes/${encodeURIComponent(pendingWriteId)}/cancel`,
    accessToken,
    signal,
    onEvent,
  });
